import { bucketKey, formatBucket, granularityForSpan } from '@lib/charts/timeBuckets';
import { useLocalization } from '@lib/localization';
import { Box, Paper, Typography } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { Clock } from 'lucide-react';
import { memo, useMemo } from 'react';

interface DailyInspectionPoint {
  date: Date;
  total: number;
};

interface Props {
  data: DailyInspectionPoint[];
  height?: number;
  color?: string;
  labelColor?: string;
  order?: 'asc' | 'desc';
};

const DEFAULT_CHART_COLOR = '#2196f3';
const DEFAULT_LABEL_COLOR = 'rgba(255,255,255,0.7)';

const InspectionActivityChart = memo(function InspectionActivityChart({
  data,
  height = 336,
  color = DEFAULT_CHART_COLOR,
  labelColor = DEFAULT_LABEL_COLOR,
  order = 'asc',
}: Props) {
  const { t, currentLanguage } = useLocalization();

  // Bars need a band axis, so each point carries the label of the bucket it lands in.
  const formattedData = useMemo(() => {
    const arr = Array.isArray(data) ? data : [];
    const granularity = granularityForSpan(arr.map((d) => new Date(d.date)));
    const buckets = new Map<string, { label: string, total: number }>();

    arr.forEach((point) => {
      const key = bucketKey(new Date(point.date), granularity);
      const bucket = buckets.get(key) ?? { label: formatBucket(key, granularity, currentLanguage), total: 0 };

      bucket.total += point.total;
      buckets.set(key, bucket);
    });

    return [...buckets.entries()]
      .sort(([a], [b]) => (order === 'asc' ? a.localeCompare(b) : b.localeCompare(a)))
      .map(([, bucket]) => bucket);
  }, [data, order, currentLanguage]);

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 2,
          px: 2,
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Clock size={24} />
          <Typography variant="h6" sx={{ ml: 1, fontWeight: 300 }}>
            {t('Statistic_InspectionActivityTimeline')}
          </Typography>
        </Box>
      </Box>

      <Paper
        sx={{
          p: 3,
          height,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <BarChart
          height={height - 86}
          dataset={formattedData}
          yAxis={[
            {
              tickMinStep: 1
            },
          ]}
          xAxis={[
            {
              dataKey: 'label',
              scaleType: 'band',
            },
          ]}
          series={[
            {
              dataKey: 'total',
              label: t('Statistic_InspectionCount'),
              color,
            },
          ]}
          borderRadius={4}
          grid={{ horizontal: true, vertical: false }}
          margin={{ left: 0, right: 0, top: 12, bottom: 0 }}
          sx={{
            '--Charts-axis-label-color': labelColor,
          }}
          slotProps={{
            tooltip: {
              sx: {
                '*': {
                  color: '#fff !important',
                },
              },
            },
          }}
        />
      </Paper>
    </Box>
  );
});

export default InspectionActivityChart;
